import { GraphModel } from '../../graph/graph.model';
import { DG } from '@cotera/sdk/core';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { Assert } from '@cotera/utilities';
import { toast } from '@cotera/client/app/components/ui/toaster';
import {
  AskResponse,
  ModelConstructor,
  TargetableModel,
} from '../../actions/framework/types';
import { COLUMN_ACTION_GROUP } from '../constants';
import { LlmColumnModel } from '../llm/llm-column.model';
import { AskToDelete } from '../llm/components/ask-to-delete';
import { ExpressionColumnModel } from '../expression/expression-column.model';
import { TaxonomyColumnModel } from '../taxonomy/taxonomy-column.model';
import { ToolCallModel } from '../tool-call/tool-call.model';

type Params = {};

type ColumnModel<FormState extends { name: string } = { name: string }> =
  TargetableModel & {
    datasetName: string;
    currentNameInGraph: Watchable<string>;
    formState: Watchable<FormState>;
  };

const COLUMN_MODEL_CLASSES: readonly ModelConstructor<ColumnModel<any>>[] = [
  ExpressionColumnModel,
  LlmColumnModel,
  TaxonomyColumnModel,
  ToolCallModel,
] as const;

function getTargetedColumn(
  context: ApplicableContext | ExecuteContext
): ColumnModel | null {
  for (const ModelClass of COLUMN_MODEL_CLASSES) {
    const model = context.targetedModelOfType(
      ModelClass as ModelConstructor<ColumnModel<any>>
    );

    if (model !== null) {
      return model;
    }
  }

  return null;
}

export class DeleteColumnAction extends BaseAction<{}, { t: string }[]> {
  override title = 'Delete Column';
  override inputSchema = z.object({});
  group = COLUMN_ACTION_GROUP;
  icon = 'trash';

  constructor(
    private readonly navigator: {
      close: (tabId: string) => void;
    },
    private readonly modalManager: {
      push: (props: {
        view: () => React.ReactNode;
        onClose?: () => void;
      }) => void;
      pop: () => void;
    }
  ) {
    super();
  }

  /**
   * Graph Model & a targeted ColumnModel must be in scope
   */
  override applicable(context: ApplicableContext): boolean {
    if (!context.isInScope(GraphModel)) {
      return false;
    }
    const targetedColumn = getTargetedColumn(context);

    return (
      targetedColumn !== null &&
      //is in graph
      targetedColumn.currentNameInGraph.snapshot() !== null
    );
  }

  override async execute(
    _: {},
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    const columnModel = getTargetedColumn(context);
    const graphModel = context.getInScopeModelOfType(GraphModel);

    // by `applicable()`
    Assert.assert(columnModel !== null);
    Assert.assert(graphModel !== null);

    const columnName = columnModel.currentNameInGraph.snapshot();

    const column = graphModel.graph.snapshot().getColumn({
      rel: columnModel.datasetName,
      name: columnName,
    });

    Assert.assert(column !== null);
    const ops = column.makeDeleteOps();
    Assert.assert(ops.isOk());

    const tempCommit: DG.Commit = {
      actorId: 'system',
      timestamp: new Date(),
      operations: ops.value,
      message: null,
    };

    const datasetCompilationResult = graphModel.compiler
      .snapshot()
      .commit(tempCommit)
      .compile();

    if (datasetCompilationResult.isErr()) {
      toast.error(`Failed to delete column ${columnName}`);
      return err([
        {
          t: 'failed-to-delete-column',
          message: JSON.stringify(datasetCompilationResult.error),
        },
      ]);
    }

    const stagingResult = await graphModel.attemptToStageOperations(ops.value);

    if (stagingResult.isErr()) {
      toast.error(`Failed to delete column ${columnName}`);
      return err([
        {
          t: 'failed-to-delete-column',
          message: JSON.stringify(stagingResult.error),
        },
      ]);
    }

    this.navigator.close(
      `/dataset/${columnModel.datasetName}/column/${columnName}`
    );

    return ok({});
  }

  shouldAsk(_: ApplicableContext): boolean {
    return true;
  }

  ask(_: {}, context: ExecuteContext): AskResponse<Params> {
    const columnModel = getTargetedColumn(context);
    Assert.assert(columnModel !== null, 'ColumnModel not found');

    return {
      view: ({ onRespond, onCancel }) => {
        this.modalManager.push({
          view: () => {
            return (
              <AskToDelete
                onRespond={async (params) => {
                  await onRespond(params);
                  this.modalManager.pop();
                }}
                itemName={columnModel.currentNameInGraph.snapshot()}
              />
            );
          },
          onClose: async () => {
            await onCancel();
          },
        });
      },
    };
  }
}
